import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {Store} from "@ngrx/store";
import {MatDialog} from "@angular/material/dialog";
import {selectDealOverviewContent, selectDealOverviewLoading, selectDealOverviewParams, selectDealOverviewTotalElements} from "../store/deal/deal.selectors";
import {DealActions, DealListActions, DealOverviewActions} from "../store/deal.actions";
import {AuthenticationService} from "../../shared/services/login/authentication.service";
import {DELETE_ACCESS} from "../../shared/model/login/auth.constants";
import {DealNewDialogComponent} from "../deal-new-dialog/deal-new-dialog.component";
import {combineLatest, filter, skip, Subject, Subscription, take} from "rxjs";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {ProjectReportDialogComponent} from "./project-report-dialog/project-report-dialog.component";
import {selectReportGenerationStatus} from "../store/report/report.selectors";
import {Sort} from "@angular/material/sort";
import {PageEvent} from "@angular/material/paginator";
import {CodeTableEnum} from "../../shared/model/code";
import {selectSettingsLoaded, selectTableColumns, selectTablePageSize, selectTableSort} from "../../shared/user-setting/store/user-setting.selectors";
import {UserSettingActions} from "../../shared/user-setting/store/user-setting.actions";
import {TableSettingsDialogComponent, TableSettingsDialogData, TableSettingsDialogResult} from "../../shared/user-setting/table-settings-dialog/table-settings-dialog.component";
import {SelectionModel} from "@angular/cdk/collections";
import {DealOverviewItem} from "../models/deal-overview-item";
import {ContextMenuComponent, ContextMenuItem} from "../../shared/components/context-menu/context-menu.component";
import {ConfirmationComponent} from "../../shared/components/confirmation/confirmation.component";

import {CommentCountsActions} from "../../comment/store/comment.actions";
import {selectSelectedSourceDataset} from "../../import/store/import.selectors";

const TABLE_KEY = "deal-overview";
const COMMENT_CONTEXT = "/deals/overview/";

const ALL_COLUMN_GROUPS = [
    {label: "Main", cols: ["dealId", "dealName", "closingDate", "currencyIso", "totalDealSize", "totalNav", "bidPrice", "discountToNav"]},
];

const COLUMN_LABELS: Record<string, string> = {
    dealId: "ID",
    dealName: "Deal",
    closingDate: "Closing Date",
    currencyIso: "Currency",
    totalDealSize: "Total Deal Size",
    totalNav: "Total NAV",
    bidPrice: "Bid Price",
    discountToNav: "Discount to NAV",
};

const SORTABLE_COLUMNS = [
    {value: "id", label: "ID"},
    {value: "projectName", label: "Deal"},
    {value: "closingDate", label: "Closing Date"},
    {value: "currencyIso", label: "Currency"},
    {value: "totalDealSize", label: "Total Deal Size"},
    {value: "totalNav", label: "Total NAV"},
    {value: "bidPrice", label: "Bid Price"},
    {value: "discountToNav", label: "Discount to NAV"},
];

@Component({
    selector: "valumize-deal-overview-page",
    templateUrl: "./deal-overview-page.component.html",
    styleUrls: ["./deal-overview-page.component.scss"],
    standalone: false,
    host: {class: "overview-page"}
})
export class DealOverviewPageComponent implements OnInit, OnDestroy {
    private static readonly ALL_VALID_COLUMNS = ALL_COLUMN_GROUPS.flatMap(g => g.cols);

    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);
    private readonly authService = inject(AuthenticationService);

    private readonly searchSubject = new Subject<string>();
    private searchSubscription?: Subscription;
    private readonly settingsSubscription?: Subscription;
    private columnsSubscription?: Subscription;
    private dialogSubscription?: Subscription;
    private commentCountsSubscription?: Subscription;

    @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;
    @ViewChild("ctxMenu") contextMenu?: ContextMenuComponent;

    readonly selection = new SelectionModel<DealOverviewItem>(true, [], true, (a, b) => a.id === b.id);
    selectMode = false;

    reportGenerationStatus$ = this.store.select(selectReportGenerationStatus);

    columns = ["dealId", "dealName", "closingDate", "currencyIso", "totalDealSize", "totalNav", "bidPrice", "discountToNav"];
    codeTableCurrency = CodeTableEnum.SHARED_CURRENCY;
    searchVisible = false;

    deals$ = this.store.select(selectDealOverviewContent);
    totalElements$ = this.store.select(selectDealOverviewTotalElements);
    params$ = this.store.select(selectDealOverviewParams);
    isLoading$ = this.store.select(selectDealOverviewLoading);

    sortActive = "projectName";
    sortDirection: "asc" | "desc" | "" = "asc";

    constructor() {
        this.settingsSubscription = this.store.select(selectSettingsLoaded).pipe(
            filter(Boolean),
            take(1)
        ).subscribe(() => {
            this.store.dispatch(DealListActions.open());
            this.applySettingsFromStore();
        });
    }

    ngOnInit() {
        this.searchSubscription = this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(search => {
            // Selection is kept across pagination (WI 992), but a search change filters the row
            // set — clear the selection so batch actions can't target now-invisible rows.
            this.selection.clear();
            this.store.dispatch(DealOverviewActions.changeSearch({search}));
        });

        this.columnsSubscription = this.store.select(selectTableColumns(TABLE_KEY)).pipe(
            skip(1),
            filter((cols): cols is string[] => !!cols)
        ).subscribe(cols => {
            const valid = this.filterValidColumns(cols);
            if (valid) this.columns = valid;
        });

        // Page-level comment indicator: load counts for this page's context, refreshed on dataset switch.
        this.commentCountsSubscription = this.store.select(selectSelectedSourceDataset).pipe(
            distinctUntilChanged()
        ).subscribe(() => this.store.dispatch(CommentCountsActions.load({inContext: COMMENT_CONTEXT})));
    }

    get displayedColumns(): string[] {
        return this.selectMode ? ["select", ...this.columns] : this.columns;
    }

    enableSelectMode(row: DealOverviewItem) {
        this.selectMode = true;
        this.selection.select(row);
    }

    exitSelectMode() {
        this.selection.clear();
        this.selectMode = false;
    }

    onArchivedChanged(checked: boolean) {
        // Toggling the archived filter changes which rows are visible — clear the selection so
        // batch actions can't target rows that left the view.
        this.selection.clear();
        this.store.dispatch(DealOverviewActions.changeArchived({archived: checked}));
    }

    onRowContextMenu(event: MouseEvent, row: DealOverviewItem) {
        // While multi-selecting, the context menu manages the clicked row's membership in the batch
        // (the toolbar handles the batch-wide actions).
        if (this.selectMode) {
            const isSelected = this.selection.isSelected(row);
            const selectItems: ContextMenuItem[] = [
                {
                    label: isSelected ? "Deselect" : "Select",
                    icon: isSelected ? "check_box_outline_blank" : "check_box",
                    action: () => this.selection.toggle(row)
                },
                {
                    label: "Clear selection",
                    icon: "deselect",
                    action: () => this.exitSelectMode()
                }
            ];
            this.contextMenu?.open(event, selectItems);
            return;
        }
        // Right-clicking a row targets just that row.
        if (!this.selection.isSelected(row)) {
            this.selection.clear();
            this.selection.select(row);
        }
        const targets = this.selection.selected;
        const allArchived = targets.every(deal => deal.isArchived?.value);
        const items: ContextMenuItem[] = [
            {
                label: "Select",
                icon: "check_box",
                action: () => this.enableSelectMode(row)
            },
            {
                label: allArchived ? "Unarchive" : "Archive",
                icon: allArchived ? "unarchive" : "archive",
                action: () => this.archiveSelected(!allArchived)
            },
            {
                label: "Delete",
                icon: "delete",
                hidden: !this.canDelete,
                action: () => this.deleteDeal(row)
            }
        ];
        this.contextMenu?.open(event, items);
    }

    get canDelete(): boolean {
        return this.authService.currentUser?.hasAnyPermission(DELETE_ACCESS) ?? false;
    }

    deleteDeal(deal: DealOverviewItem) {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = "Delete Deal";
        dialogRef.componentInstance.htmlContent =
            "<p>Are you sure you want to delete this deal?</p><p><strong>This action cannot be undone.</strong></p>";
        dialogRef.afterClosed().pipe(take(1)).subscribe(confirmed => {
            if (confirmed) {
                this.store.dispatch(DealActions.delete({dealId: deal.id}));
            }
        });
    }

    deleteSelected() {
        const deals = this.selection.selected;
        if (deals.length === 0) {
            return;
        }
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `Delete ${deals.length} deal${deals.length > 1 ? "s" : ""}?`;
        dialogRef.componentInstance.htmlContent =
            `<p>Are you sure you want to delete ${deals.length} deal${deals.length > 1 ? "s" : ""}?</p><p><strong>This action cannot be undone.</strong></p>`;
        dialogRef.afterClosed().pipe(take(1)).subscribe(confirmed => {
            if (confirmed) {
                this.store.dispatch(DealOverviewActions.deleteDeals({deals}));
                this.selection.clear();
                this.selectMode = false;
            }
        });
    }

    archiveSelected(archived: boolean) {
        const deals = this.selection.selected;
        if (deals.length === 0) {
            return;
        }
        if (deals.length === 1) {
            this.dispatchArchive(deals, archived);
            return;
        }
        const verb = archived ? "Archive" : "Unarchive";
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `${verb} ${deals.length} deals?`;
        dialogRef.componentInstance.htmlContent = `<p>Are you sure you want to ${verb.toLowerCase()} these ${deals.length} deals?</p>`;
        dialogRef.afterClosed().pipe(take(1)).subscribe(confirmed => {
            if (confirmed) {
                this.dispatchArchive(deals, archived);
            }
        });
    }

    private dispatchArchive(deals: DealOverviewItem[], archived: boolean) {
        this.store.dispatch(DealOverviewActions.archiveDeals({deals, archived}));
        this.selection.clear();
        this.selectMode = false;
    }

    onSearchInput(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.searchSubject.next(value);
    }

    onSortChange(sort: Sort) {
        if (sort.direction) {
            this.sortActive = sort.active;
            this.sortDirection = sort.direction;
        } else {
            this.sortActive = "projectName";
            this.sortDirection = "asc";
        }
        const sortString = `${this.sortActive},${this.sortDirection}`;
        this.store.dispatch(DealOverviewActions.changeSort({sort: sortString}));
    }

    onPageChange(event: PageEvent) {
        this.store.dispatch(DealOverviewActions.changePage({page: event.pageIndex, size: event.pageSize}));
    }

    toggleSearch() {
        this.searchVisible = !this.searchVisible;
        if (this.searchVisible) {
            setTimeout(() => this.searchInput?.nativeElement.focus());
        } else {
            this.searchSubject.next("");
        }
    }

    openTableSettings() {
        this.store.select(selectDealOverviewParams).pipe(take(1)).subscribe(params => {
            const sortParts = params.sort.split(",");
            const currentSort = {field: sortParts[0] || "projectName", direction: sortParts[1] === "desc" ? "desc" : "asc"} as const;

            const dialogData: TableSettingsDialogData = {
                tableKey: TABLE_KEY,
                columnGroups: ALL_COLUMN_GROUPS,
                columnLabels: COLUMN_LABELS,
                currentColumns: this.columns,
                currentPageSize: params.size,
                currentSort,
                pageSizeOptions: [10, 20, 30, 50, 100],
                sortableColumns: SORTABLE_COLUMNS,
                isAdmin: false,
                defaultColumns: DealOverviewPageComponent.ALL_VALID_COLUMNS,
                defaultPageSize: 20,
                defaultSort: {field: "projectName", direction: "asc"},
            };

            this.dialogSubscription = this.dialog.open(TableSettingsDialogComponent, {data: dialogData, width: "520px"})
                .afterClosed()
                .subscribe((result: TableSettingsDialogResult) => this.handleDialogResult(result));
        });
    }

    openNewDealDialog() {
        this.dialog.open(DealNewDialogComponent);
    }

    openProjectReportDialog() {
        this.dialog.open(ProjectReportDialogComponent, {width: "20%"});
    }

    ngOnDestroy() {
        this.searchSubscription?.unsubscribe();
        this.settingsSubscription?.unsubscribe();
        this.columnsSubscription?.unsubscribe();
        this.dialogSubscription?.unsubscribe();
        this.commentCountsSubscription?.unsubscribe();
    }

    private applySettingsFromStore() {
        combineLatest([
            this.store.select(selectTableColumns(TABLE_KEY)),
            this.store.select(selectTablePageSize(TABLE_KEY)),
            this.store.select(selectTableSort(TABLE_KEY))
        ]).pipe(take(1)).subscribe(([columns, pageSize, sort]) => {
            if (columns) {
                const valid = this.filterValidColumns(columns);
                if (valid) this.columns = valid;
            }
            if (sort) {
                this.sortActive = sort.field;
                this.sortDirection = sort.direction as "asc" | "desc";
            }
            this.store.dispatch(DealOverviewActions.applySettings({
                size: pageSize ?? undefined,
                sort: sort ? `${sort.field},${sort.direction}` : undefined
            }));
            this.store.dispatch(DealOverviewActions.search());
        });
    }

    private filterValidColumns(columns: string[]): string[] | null {
        const valid = columns.filter(c => DealOverviewPageComponent.ALL_VALID_COLUMNS.includes(c));
        return valid.length > 0 ? valid : null;
    }

    private handleDialogResult(result: TableSettingsDialogResult) {
        if (!result) return;

        this.columns = result.columns;
        this.sortActive = result.sort.field;
        this.sortDirection = result.sort.direction as "asc" | "desc";
        this.store.dispatch(DealOverviewActions.applySettings({
            size: result.pageSize,
            sort: `${result.sort.field},${result.sort.direction}`
        }));
        this.store.dispatch(DealOverviewActions.search());

        const payload = {tableKey: TABLE_KEY, columns: result.columns, pageSize: result.pageSize, sort: result.sort};

        if (result.saveAdmin) {
            this.store.dispatch(UserSettingActions.saveAdminTableSettings(payload));
        }
        if (result.saveUser) {
            this.store.dispatch(UserSettingActions.saveTableSettings(payload));
        }
        if (!result.saveUser && !result.saveAdmin) {
            this.store.dispatch(UserSettingActions.applyTableSettings(payload));
        }
    }
}
